import { Box, Group, Text, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "react-i18next";

import lockerIcon from "@/assets/icons/locker.svg";

import type { CustomLegendProps } from "../../interfaces/PerformanceChart.types";

export const CustomLegend = ({
  payload,
  performanceName,
  showLockerInLegend = false,
  openPlanModal,
  getBenchmarkColor,
}: CustomLegendProps) => {
  const { t } = useTranslation();

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  if (!payload || !Array.isArray(payload)) return null;

  return (
    <Group gap="xl" align="center" style={{ paddingTop: 8 }}>
      {payload.map((entry) => {
        const entryValue = entry?.value ?? "";
        const labelText = entry?.payload?.payload?.label ?? entryValue;

        const showLocker =
          showLockerInLegend && !!performanceName && String(entryValue) === String(performanceName);

        const rawColor = entry?.color;

        let cssColor = "var(--mantine-color-primary-5)";

        if (rawColor) {
          const s = String(rawColor);

          if (s.startsWith("#")) {
            cssColor = s;
          } else if (s.startsWith("var(")) {
            cssColor = s;
          } else {
            if (s.includes(".")) {
              const token = s.replace(/\./g, "-");
              cssColor = `var(--mantine-color-${token})`;
            } else {
              cssColor = `var(--mantine-color-${s}-5)`;
            }
          }
        } else if (performanceName && String(entryValue) === String(performanceName)) {
          const fallback = getBenchmarkColor(performanceName);
          const fb = String(fallback);
          cssColor = fb.startsWith("#") ? fb : `var(--mantine-color-${fb.replace(/\./g, "-")})`;
        }

        return (
          <Group key={String(entryValue)} gap="sm" style={{ alignItems: "center" }}>
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: cssColor,
              }}
            />

            <Text
              size="xs"
              style={{
                color: isDark ? "#fff" : "var(--mantine-color-secondary-6)",
                textTransform: "none",
              }}
            >
              {labelText}
            </Text>

            {showLocker && (
              <Group
                gap={6}
                align="center"
                style={{ marginLeft: 6, cursor: "pointer" }}
                onClick={openPlanModal}
              >
                <Box
                  component="img"
                  src={lockerIcon as unknown as string}
                  alt={t("plans.contentLocker.upgradeToAccess")}
                  style={{
                    width: 22,
                    height: 22,
                    objectFit: "contain",
                    display: "inline-block",
                  }}
                />

                <Text size="xs" c="dimmed">
                  {t("components.PerformanceChart.unlockOtherAnalysis", "Unlock other analysis")}
                </Text>
              </Group>
            )}
          </Group>
        );
      })}
    </Group>
  );
};
